import React, { useState, useRef, useEffect } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { ColumnDefinition } from '../../features/table-preferences/types';
import './ColumnManager.scss';

interface ColumnManagerProps<T = any> {
  columns: ColumnDefinition<T>[];
  columnVisibility: Record<string, boolean>;
  onToggleColumn: (columnId: string) => void;
  onShowAll: () => void;
  onHideAll: () => void;
  onReorderColumn?: (activeId: string, overId: string) => void;
  isUpdating?: boolean;
}

interface SortableColumnItemProps<T = any> {
  column: ColumnDefinition<T>;
  isVisible: boolean;
  isDisabled: boolean;
  isUpdating: boolean;
  onToggle: (id: string) => void;
}

function SortableColumnItem<T = any>({
  column,
  isVisible,
  isDisabled,
  isUpdating,
  onToggle,
}: SortableColumnItemProps<T>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`column-manager__item ${isDisabled ? 'column-manager__item--disabled' : ''}`}
      title={isDisabled ? 'At least one column must be visible' : ''}
    >
      <button
        type="button"
        className="column-manager__drag-handle"
        {...attributes}
        {...listeners}
        aria-label={`Drag to reorder ${column.header}`}
      >
        <GripVertical size={14} />
      </button>
      <label className="column-manager__item-label">
        <input
          type="checkbox"
          checked={isVisible}
          onChange={() => onToggle(column.id)}
          disabled={isUpdating || isDisabled}
          className="column-manager__checkbox"
        />
        <span className="column-manager__label">{column.header}</span>
      </label>
    </div>
  );
}

/**
 * Dropdown component for managing table column visibility
 */
export function ColumnManager<T = any>({
  columns,
  columnVisibility,
  onToggleColumn,
  onShowAll,
  onHideAll,
  onReorderColumn,
  isUpdating = false,
}: ColumnManagerProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const visibleCount = Object.values(columnVisibility).filter(Boolean).length;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorderColumn?.(String(active.id), String(over.id));
  };

  return (
    <div className="column-manager" ref={dropdownRef}>
      <button
        className="btn btn--ghost column-manager__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-label="Manage columns"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 3h12M2 8h12M2 13h12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        Columns ({visibleCount}/{columns.length})
      </button>

      {isOpen && (
        <div className="column-manager__dropdown">
          <div className="column-manager__header">
            <span className="column-manager__title">Manage columns</span>
            <button
              className="column-manager__close"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="column-manager__actions">
            <button
              className="column-manager__action-btn"
              onClick={onShowAll}
              disabled={isUpdating}
            >
              Show all
            </button>
            <button
              className="column-manager__action-btn"
              onClick={onHideAll}
              disabled={isUpdating}
            >
              Hide all
            </button>
          </div>

          <div className="column-manager__list">
            {onReorderColumn ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={columns.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {columns.map((column) => {
                    const isVisible = columnVisibility[column.id] ?? true;
                    const isOnlyVisible =
                      isVisible && Object.values(columnVisibility).filter(Boolean).length === 1;

                    return (
                      <SortableColumnItem
                        key={column.id}
                        column={column}
                        isVisible={isVisible}
                        isDisabled={isOnlyVisible}
                        isUpdating={isUpdating}
                        onToggle={onToggleColumn}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            ) : (
              columns.map((column) => {
                const isVisible = columnVisibility[column.id] ?? true;
                const isOnlyVisible =
                  isVisible && Object.values(columnVisibility).filter(Boolean).length === 1;

                return (
                  <label
                    key={column.id}
                    className={`column-manager__item ${
                      isOnlyVisible ? 'column-manager__item--disabled' : ''
                    }`}
                    title={isOnlyVisible ? 'At least one column must be visible' : ''}
                  >
                    <input
                      type="checkbox"
                      checked={isVisible}
                      onChange={() => onToggleColumn(column.id)}
                      disabled={isUpdating || isOnlyVisible}
                      className="column-manager__checkbox"
                    />
                    <span className="column-manager__label">{column.header}</span>
                  </label>
                );
              })
            )}
          </div>

          {isUpdating && (
            <div className="column-manager__saving">
              <div className="column-manager__spinner" />
              Saving...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
